import { prefillRefusedSentence } from '@/lib/npc-labels';
import type { NpcPrefillReason, SessionDetail } from '@matter_extractor/shared';

/**
 * What the head of a session says of « Créer un NPC » (npc.md §4.2, §13.3, H18, issue #210) -
 * pure, and nothing of the NPC page itself: the gesture that leads to it and the reason it is
 * greyed.
 *
 * ## The reasons are the SERVER's, decided on the screen
 *
 * `GET /api/npcs/prefill` refuses with a 409 `prefill-refused` whose `reason` is one of four
 * (`NpcPrefillReason`), after a fifth that is the door of the exports - `session-in-trash`. The
 * button is greyed with the SAME reasons, in the same order as `prefillRefusalOf`, read off what the
 * detail of the session already holds: no request is spent to say « no », and the page the gesture
 * opens still words a refusal that slipped through (a session that moved between two reads).
 *
 * The order is the server's: the trash first (restore it), then a replay (it is already an NPC's),
 * then a session still running, then one with no protocol page, then one with no turn - a reread on
 * entry is no turn (#165), the same measure as the server's.
 */

/** A reason the greyed gesture can give: the four of the contract, and the trash. */
export type NpcEntryReason = NpcPrefillReason | 'session-in-trash';

export type NpcEntryState =
  /** Not drawn: the replay is closed on this server, or this is the vitrine. */
  | { readonly kind: 'absent' }
  | { readonly kind: 'open' }
  | { readonly kind: 'disabled'; readonly reason: NpcEntryReason; readonly sentence: string };

/** The sentence of a session in the trash, the server's own for the refusal (`npcRefusalSentence`). */
export const NPC_ENTRY_TRASH_SENTENCE = 'Cette session est à la corbeille : restaurez-la d’abord.';

/** The line under the open gesture (maquette `DepuisSession`): what it does. */
export const NPC_ENTRY_HINT = 'Ses mots deviennent la source d’un NPC neuf, en brouillon.';

/** What the gesture needs to decide: the row of the session and its turns. */
export type NpcEntrySession = Pick<SessionDetail, 'turns'> & {
  readonly session: Pick<
    SessionDetail['session'],
    'status' | 'protocolId' | 'deletedAt' | 'replay'
  >;
};

export function npcEntryReason(detail: NpcEntrySession): NpcEntryReason | null {
  const { session } = detail;
  if (session.deletedAt !== null) return 'session-in-trash';
  if (session.replay !== undefined && session.replay !== null) return 'replay';
  if (session.status === 'in-progress') return 'in-progress';
  if (session.protocolId === null) return 'no-protocol-page';
  if (!detail.turns.some((turn) => !turn.reread)) return 'no-turn';
  return null;
}

/**
 * The state of the gesture: `available` is whether the NPC exist HERE (the replay open and not the
 * vitrine, which the screen reads), and a server without them draws nothing at all.
 */
export function npcEntryState(detail: NpcEntrySession, available: boolean): NpcEntryState {
  if (!available) return { kind: 'absent' };
  const reason = npcEntryReason(detail);
  if (reason === null) return { kind: 'open' };
  return {
    kind: 'disabled',
    reason,
    sentence:
      reason === 'session-in-trash' ? NPC_ENTRY_TRASH_SENTENCE : prefillRefusedSentence(reason),
  };
}
